import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

export default function Dashboard() {
  const [searchParams] = useSearchParams();
  const [showSuccess, setShowSuccess] = useState(false);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      setShowSuccess(true);
      // Clear the session_id from URL after showing success
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Success Message */}
          {showSuccess && (
            <div className="mb-8 bg-green-50 border-2 border-green-200 rounded-2xl p-6 animate-fade-in">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Premium Activated!</h3>
                  <p className="text-sm text-green-800">
                    Your account has been upgraded to Premium. You now have unlimited access to all features.
                  </p>
                </div>
                <button
                  onClick={() => setShowSuccess(false)}
                  className="text-green-600 hover:text-green-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Welcome to Your Dashboard</h1>
            <p className="text-base text-gray-600">Start analyzing messages and building healthy communication skills</p>
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Link
              to="/analyzer"
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-chat-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyze Message</h3>
              <p className="text-sm text-gray-600 mb-4">Get instant insights about tone, intent, and reply suggestions</p>
              <span className="text-sm text-blue-600 font-medium whitespace-nowrap">
                Start Analysis <i className="ri-arrow-right-line ml-1"></i>
              </span>
            </Link>

            <Link
              to="/learning"
              className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Learning Center</h3>
              <p className="text-sm text-gray-600 mb-4">Build skills for healthy relationships and communication</p>
              <span className="text-sm text-green-600 font-medium whitespace-nowrap">
                Start Learning <i className="ri-arrow-right-line ml-1"></i>
              </span>
            </Link>

            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 shadow-sm text-white">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-vip-crown-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">Premium Features</h3>
              <p className="text-sm text-blue-100 mb-4">Unlimited analyses, advanced insights, and priority support</p>
              <Link
                to="/checkout?plan=premium"
                className="inline-block text-sm text-white font-medium whitespace-nowrap cursor-pointer"
              >
                Upgrade Now <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Activity</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-1">0</div>
                <div className="text-sm text-gray-600">Messages Analyzed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-1">0</div>
                <div className="text-sm text-gray-600">Lessons Completed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-1">0</div>
                <div className="text-sm text-gray-600">Red Flags Detected</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900 mb-1">Free</div>
                <div className="text-sm text-gray-600">Current Plan</div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Recent Activity</h2>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-inbox-line text-3xl text-gray-400"></i>
              </div>
              <p className="text-sm text-gray-600 mb-6">No activity yet. Start by analyzing your first message!</p>
              <Link
                to="/analyzer"
                className="inline-block px-6 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Analyze Your First Message
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
